import { StateCreator } from "zustand";
import { createDesignSpecSnapshot } from "@/lib/agent/designSpecSnapshot";
import type { RenderedDesignSpec } from "@/types/generatedImage";
import type { CreateFormData } from "@/components/create/types";
import { logger } from "@/utils/logger";
import type { CreatePageStore } from "../createPageStore";

export interface GenerationActions {
  generateStart: (
    currentStep: number,
    isChatMode?: boolean,
    options?: { preserveExistingPreviews?: boolean }
  ) => void;
  generateFrontSuccess: (
    payload: {
      variations: Array<{ imageUrl: string; imagePath: string | null }>;
      designSpec: RenderedDesignSpec;
      formUpdates?: Partial<
        Pick<
          CreateFormData,
          "sleeve" | "length" | "neckline" | "silhouette" | "fabric" | "color"
        >
      >;
      warnings?: Array<{ variation: number; reason: string; error?: string }>;
      partial_failure?: boolean;
    },
    isChatMode?: boolean
  ) => void;
  generateBackSuccess: (
    payload: {
      variations: Array<{ imageUrl: string; imagePath: string | null }>;
      designSpec: RenderedDesignSpec;
      warnings?: Array<{ variation: number; reason: string; error?: string }>;
      partial_failure?: boolean;
    },
    isChatMode?: boolean
  ) => void;
  generateError: (error: string) => void;
  markFailedSignalConsumed: () => void;
  setGenerationPhase: (phase: "idle" | "front" | "back" | "complete") => void;
  setProgress: (progress: number) => void;
  setGenerationTiming: (timing: {
    startTime: number | null;
    estimatedTime: number | null;
  }) => void;
  setGenerationBlocked: (blocked: boolean, cooldownTime: number) => void;
  setLoading: (loading: boolean) => void;
  selectFrontVariation: (index: number) => void;
  selectBackVariation: (index: number) => void;
  setSelectingVariation: (selecting: boolean) => void;
  modifyStart: (side?: "front" | "back") => void;
  modifyError: (error: string) => void;
  modifySuccess: (payload: {
    imageUrl: string;
    imagePath: string | null;
    isFront: boolean;
    designSpec?: RenderedDesignSpec;
  }) => void;
  modifyFrontSuccess: (payload: {
    variations: Array<{ imageUrl: string; imagePath: string | null }>;
    designSpec: RenderedDesignSpec;
  }) => void;
  modifyBackSuccess: (payload: {
    variations: Array<{ imageUrl: string; imagePath: string | null }>;
    designSpec: RenderedDesignSpec;
  }) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearWarnings: () => void;
}

export const createGenerationSlice: StateCreator<
  CreatePageStore,
  [["zustand/immer", never]],
  [],
  GenerationActions
> = set => ({
  // Image generation actions
  generateStart: (
    currentStep: number,
    isChatMode?: boolean,
    options?: { preserveExistingPreviews?: boolean }
  ) =>
    set((state: CreatePageStore) => {
      state.generation.progress.loading = true;
      state.generation.progress.progress = 0;
      state.generation.progress.hasFailedGeneration = false;
      state.generation.progress.startTime = Date.now();
      state.generation.progress.phase = currentStep === 2 ? "front" : "back";

      // All generations use gemini-3-pro-image-preview (45s ETA)
      state.generation.progress.estimatedTimeRemaining = 45;

      // Ticket IDs are indexed against the active side's variations. Clear
      // them whenever a new side generation starts so front IDs cannot be
      // mistaken for server-persisted back IDs (or vice versa).
      if (
        (currentStep === 2 || currentStep === 4) &&
        !options?.preserveExistingPreviews
      ) {
        const previousVariationTicketIds = {
          ...state.ticket.variationTicketIds,
        };
        state.ticket.variationTicketIds = {};
        logger.info(
          `GENERATE_START action cleared variationTicketIds for step ${currentStep}`,
          {
            component: "CreatePageStore",
            metadata: {
              currentStep,
              previousVariationTicketIds,
              previousCount: Object.keys(previousVariationTicketIds).length,
              cleared: true,
            },
          }
        );
      }

      // Only clear images if starting fresh generation
      if (currentStep === 2 && !options?.preserveExistingPreviews) {
        state.generation.images.front.url = null;
        state.generation.images.front.path = null;
        state.generation.images.front.designSpec = null;
        state.generation.images.back.url = null;
        state.generation.images.back.path = null;
        state.generation.images.back.designSpec = null;
        state.generation.images.variations.front = [];
        state.generation.images.variations.back = [];
        state.generation.images.selected.frontIndex = null;
        state.generation.images.selected.backIndex = null;
      }
      if (currentStep === 4 && !options?.preserveExistingPreviews) {
        state.generation.images.back.url = null;
        state.generation.images.back.path = null;
        state.generation.images.back.designSpec = null;
        state.generation.images.variations.back = [];
        state.generation.images.selected.backIndex = null;
      }

      // Clear errors
      state.generation.errors.message = null;
    }),

  generateFrontSuccess: (payload, isChatMode?: boolean) =>
    set((state: CreatePageStore) => {
      // In-place rerenders kept the previous image and ticket until success.
      state.ticket.variationTicketIds = {};
      const designSpec = createDesignSpecSnapshot(payload.designSpec);
      const variations = payload.variations.map(variation => ({
        ...variation,
        designSpec,
      }));
      state.generation.images.variations.front = variations;
      state.generation.images.selected.frontIndex = 0; // Auto-select first variation
      // Set first variation as default display
      state.generation.images.front.url = variations[0]?.imageUrl || null;
      state.generation.images.front.path = variations[0]?.imagePath || null;
      state.generation.images.front.designSpec =
        variations[0]?.designSpec ?? null;
      if (payload.formUpdates) {
        Object.assign(state.form.data, payload.formUpdates);
      }
      if (
        !state.generation.images.front.original.url &&
        !state.generation.images.front.original.path &&
        variations[0]
      ) {
        state.generation.images.front.original.url = variations[0].imageUrl;
        state.generation.images.front.original.path = variations[0].imagePath;
        state.generation.images.front.original.designSpec =
          variations[0].designSpec;
      }
      state.generation.progress.progress = 100;
      state.generation.progress.phase = "complete";
      state.generation.progress.estimatedTimeRemaining = null;
      state.generation.progress.loading = false;
      // A render can land after the stall watchdog already failed the turn.
      // The image supersedes that failure, or the agent would narrate a
      // failure on the next turn for a dress the customer can see.
      state.generation.progress.hasFailedGeneration = false;
      state.generation.errors.message = null;
      // Track when image was generated - prevents auto-regeneration without user input
      state.generation.progress.lastGenerationCompletedAt = Date.now();
      // Only change step if not in chat mode - in chat mode, stay on step 1
      if (!isChatMode) {
        state.form.currentStep = 2;
      }
      // Set warnings and partial failure
      state.generation.errors.warnings = payload.warnings || [];
      state.generation.errors.partialFailure = payload.partial_failure || false;
    }),

  generateBackSuccess: (payload, isChatMode?: boolean) =>
    set((state: CreatePageStore) => {
      // An in-place refresh retained the previous Back (and its ticket ids)
      // while loading. Replace that mapping only once the new render succeeds.
      state.ticket.variationTicketIds = {};
      const designSpec = createDesignSpecSnapshot(payload.designSpec);
      const variations = payload.variations.map(variation => ({
        ...variation,
        designSpec,
      }));
      state.generation.images.variations.back = variations;
      state.generation.images.selected.backIndex = 0; // Auto-select first variation
      // Set first variation as default display
      state.generation.images.back.url = variations[0]?.imageUrl || null;
      state.generation.images.back.path = variations[0]?.imagePath || null;
      state.generation.images.back.designSpec =
        variations[0]?.designSpec ?? null;
      if (
        !state.generation.images.back.original.url &&
        !state.generation.images.back.original.path &&
        variations[0]
      ) {
        state.generation.images.back.original.url = variations[0].imageUrl;
        state.generation.images.back.original.path = variations[0].imagePath;
        state.generation.images.back.original.designSpec =
          variations[0].designSpec;
      }
      state.generation.progress.progress = 100;
      state.generation.progress.phase = "complete";
      state.generation.progress.estimatedTimeRemaining = null;
      state.generation.progress.loading = false;
      state.generation.progress.hasFailedGeneration = false;
      state.generation.errors.message = null;
      // Track when image was generated - prevents auto-regeneration without user input
      state.generation.progress.lastGenerationCompletedAt = Date.now();
      // Only change step if not in chat mode - in chat mode, stay on current step
      if (!isChatMode) {
        state.form.currentStep = 4;
      }
      // Set warnings and partial failure
      state.generation.errors.warnings = payload.warnings || [];
      state.generation.errors.partialFailure = payload.partial_failure || false;
      // Default to back view when successfully generating back image
      state.ui.view = "back";
    }),

  markFailedSignalConsumed: () =>
    set((state: CreatePageStore) => {
      state.generation.progress.failedSignalConsumed = true;
    }),

  generateError: (error: string) =>
    set((state: CreatePageStore) => {
      const currentPhase = state.generation.progress.phase;
      state.generation.progress.loading = false;
      state.generation.progress.hasFailedGeneration = true;
      state.generation.progress.failedSignalConsumed = false;
      state.generation.progress.progress = 0;
      state.generation.progress.phase = "idle";
      state.generation.progress.estimatedTimeRemaining = null;
      state.generation.errors.message = error;
      state.generation.errors.warnings = [];
      state.generation.errors.partialFailure = false;
      // Stay on the corresponding preview step for retry options
      state.form.currentStep = currentPhase === "front" ? 2 : 4;
    }),

  setGenerationPhase: (phase: "idle" | "front" | "back" | "complete") =>
    set((state: CreatePageStore) => {
      state.generation.progress.phase = phase;
    }),

  setProgress: (progress: number) =>
    set((state: CreatePageStore) => {
      state.generation.progress.progress = progress;
    }),

  setGenerationTiming: timing =>
    set((state: CreatePageStore) => {
      state.generation.progress.startTime = timing.startTime;
      state.generation.progress.estimatedTimeRemaining = timing.estimatedTime;
    }),

  setGenerationBlocked: (blocked: boolean, cooldownTime: number) =>
    set((state: CreatePageStore) => {
      state.generation.progress.isBlocked = blocked;
      state.generation.progress.cooldownTime = cooldownTime;
    }),

  setLoading: (loading: boolean) =>
    set((state: CreatePageStore) => {
      state.generation.progress.loading = loading;
    }),

  // Variation actions
  selectFrontVariation: (index: number) =>
    set((state: CreatePageStore) => {
      const variations = state.generation.images.variations.front;
      if (
        variations &&
        variations.length > 0 &&
        index >= 0 &&
        index < variations.length
      ) {
        const selectedVariation = variations[index];
        state.generation.images.selected.frontIndex = index;
        state.generation.images.front.url = selectedVariation.imageUrl;
        state.generation.images.front.path = selectedVariation.imagePath;
        state.generation.images.front.designSpec = selectedVariation.designSpec;
      }
    }),

  selectBackVariation: (index: number) =>
    set((state: CreatePageStore) => {
      const variations = state.generation.images.variations.back;
      if (
        variations &&
        variations.length > 0 &&
        index >= 0 &&
        index < variations.length
      ) {
        const selectedVariation = variations[index];
        state.generation.images.selected.backIndex = index;
        state.generation.images.back.url = selectedVariation.imageUrl;
        state.generation.images.back.path = selectedVariation.imagePath;
        state.generation.images.back.designSpec = selectedVariation.designSpec;
      }
    }),

  setSelectingVariation: (selecting: boolean) =>
    set((state: CreatePageStore) => {
      state.generation.images.isSelectingVariation = selecting;
    }),

  // Modification actions
  modifyStart: side =>
    set((state: CreatePageStore) => {
      state.ui.modification.isModifying = true;
      state.generation.progress.progress = 0;
      // Capture the side at dispatch. The customer may switch views while the
      // request is running, so its progress must not follow the selected tab.
      state.generation.progress.phase = side ?? state.ui.view;
      state.generation.errors.message = null;
      state.generation.progress.startTime = Date.now();
      // All generations use gemini-3-pro-image-preview (45s ETA)
      state.generation.progress.estimatedTimeRemaining = 45;
    }),

  modifyError: (error: string) =>
    set((state: CreatePageStore) => {
      state.ui.modification.isModifying = false;
      state.generation.errors.message = error;
    }),

  modifySuccess: payload =>
    set((state: CreatePageStore) => {
      if (payload.isFront) {
        state.generation.images.front.url = payload.imageUrl;
        state.generation.images.front.path = payload.imagePath;
        state.generation.images.front.designSpec = createDesignSpecSnapshot(
          payload.designSpec
        );
      } else {
        state.generation.images.back.url = payload.imageUrl;
        state.generation.images.back.path = payload.imagePath;
        state.generation.images.back.designSpec = createDesignSpecSnapshot(
          payload.designSpec
        );
      }
      state.generation.progress.progress = 100;
      state.generation.progress.phase = "complete";
      state.generation.progress.startTime = null;
      state.generation.progress.estimatedTimeRemaining = null;
      state.ui.modification.prompt = "";
      state.ui.modification.showInput = false;
      state.ui.modification.isModifying = false;
      // Track when modification completed - prevents auto-regeneration without user input
      state.generation.progress.lastGenerationCompletedAt = Date.now();
    }),

  modifyFrontSuccess: payload =>
    set((state: CreatePageStore) => {
      const designSpec = createDesignSpecSnapshot(payload.designSpec);
      const variations = payload.variations.map(variation => ({
        ...variation,
        designSpec,
      }));
      state.generation.images.variations.front = variations;
      state.generation.images.selected.frontIndex = 0; // Auto-select first variation
      // Set first variation as default display
      state.generation.images.front.url = variations[0]?.imageUrl || null;
      state.generation.images.front.path = variations[0]?.imagePath || null;
      state.generation.images.front.designSpec =
        variations[0]?.designSpec ?? null;
      state.generation.progress.progress = 100;
      state.generation.progress.phase = "complete";
      state.generation.progress.startTime = null;
      state.generation.progress.estimatedTimeRemaining = null;
      state.ui.modification.prompt = "";
      state.ui.modification.showInput = false;
      state.ui.modification.isModifying = false;
      // Track when modification completed - prevents auto-regeneration without user input
      state.generation.progress.lastGenerationCompletedAt = Date.now();
    }),

  modifyBackSuccess: payload =>
    set((state: CreatePageStore) => {
      const designSpec = createDesignSpecSnapshot(payload.designSpec);
      const variations = payload.variations.map(variation => ({
        ...variation,
        designSpec,
      }));
      state.generation.images.variations.back = variations;
      state.generation.images.selected.backIndex = 0; // Auto-select first variation
      // Set first variation as default display
      state.generation.images.back.url = variations[0]?.imageUrl || null;
      state.generation.images.back.path = variations[0]?.imagePath || null;
      state.generation.images.back.designSpec =
        variations[0]?.designSpec ?? null;
      state.generation.progress.progress = 100;
      state.generation.progress.phase = "complete";
      state.generation.progress.startTime = null;
      state.generation.progress.estimatedTimeRemaining = null;
      state.ui.modification.prompt = "";
      state.ui.modification.showInput = false;
      state.ui.modification.isModifying = false;
      // Track when modification completed - prevents auto-regeneration without user input
      state.generation.progress.lastGenerationCompletedAt = Date.now();
    }),

  // Error actions
  setError: (error: string | null) =>
    set((state: CreatePageStore) => {
      state.generation.errors.message = error;
    }),

  clearError: () =>
    set((state: CreatePageStore) => {
      state.generation.errors.message = null;
    }),

  clearWarnings: () =>
    set((state: CreatePageStore) => {
      state.generation.errors.warnings = [];
      state.generation.errors.partialFailure = false;
    }),
});
